import { useState, useEffect, useRef } from 'react';
import { CreateButton } from '../../../CommonObjects';
import axios from '../../../../api/apiClient';
import { QRCodeCanvas } from 'qrcode.react';

// Import existing reusable components
import { GeneralMessage, useGeneralMessage } from '../../../../common/APICommon';
import { LoadingSpinner } from '../../../../common/Loading';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import QrCodeIcon from '@mui/icons-material/QrCode';

// ==================== REUSABLE COMPONENTS ====================

/**
 * Canvas-based image generator with fixed original size
 */
export function CanvasImageGenerator({ 
  canvasRef, 
  templateSrc, 
  images = [], 
  textItems = [],
  canvasWidth = 375,  // Original fixed width
  canvasHeight = 600, // Original fixed height
  onLoad 
}) {
  useEffect(() => {
    const canvas = canvasRef.current;
    
    if (!canvas) return;

    // Set fixed canvas size (original ID dimensions)
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const context = canvas.getContext('2d');
    const imagePromises = [];

    // Load template image
    const templateImage = new Image();
    templateImage.src = templateSrc;
    imagePromises.push(new Promise((resolve, reject) => {
      templateImage.onload = resolve;
      templateImage.onerror = reject;
    }));

    // Load additional images
    const loadedImages = [templateImage];
    images.forEach(img => {
      const image = new Image();
      image.src = img.src;
      loadedImages.push(image);
      
      imagePromises.push(new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      }));
    });

    Promise.all(imagePromises)
      .then(() => {
        // Draw template with fixed size
        context.drawImage(templateImage, 0, 0, canvasWidth, canvasHeight);
        
        // Draw additional images with original positions
        images.forEach((img, index) => {
          if (img.x !== undefined && img.y !== undefined && img.width && img.height) {
            context.drawImage(
              loadedImages[index + 1], // +1 because template is first
              img.x,
              img.y,
              img.width,
              img.height
            );
            
            // Optional border
            if (img.border) {
              context.strokeStyle = img.border.color || 'black';
              context.lineWidth = img.border.width || 1;
              context.strokeRect(img.x, img.y, img.width, img.height);
            }
          }
        });
        
        // Draw text items with original positions
        textItems.forEach(text => {
          if (text.font) context.font = text.font;
          if (text.fillStyle) context.fillStyle = text.fillStyle;
          if (text.textAlign) context.textAlign = text.textAlign;
          if (text.maxWidth) {
            // Text wrapping logic
            const words = text.content.split(' ');
            let line = '';
            let lineY = text.y;
            
            for (let i = 0; i < words.length; i++) {
              const testLine = line + words[i] + ' ';
              const metrics = context.measureText(testLine);
              
              if (metrics.width > text.maxWidth && i > 0) {
                context.fillText(line, text.x, lineY);
                line = words[i] + ' ';
                lineY += text.lineHeight || 20;
              } else {
                line = testLine;
              }
            }
            context.fillText(line, text.x, lineY);
          } else {
            context.fillText(text.content, text.x, text.y);
          }
        });
        
        onLoad?.();
      })
      .catch(error => {
        console.error('Failed to load images:', error);
      });
  }, [templateSrc, images, textItems, canvasWidth, canvasHeight]);

  return (
    <canvas 
      ref={canvasRef} 
      style={{ 
        border: '1px solid black', 
        width: `${canvasWidth}px`, 
        height: `${canvasHeight}px` 
      }} 
    />
  );
}

/**
 * QR Code modal component
 */
export function QRCodeModal({ isOpen, qrValue, onClose, onDownload }) {
  if (!isOpen) return null;

  const handleDownload = () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');

    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `qr-code-${Date.now()}.png`;
    
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    
    onDownload?.();
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
      <div className="modal">
        <div className="popUp-header row">
          <div className="popUp-title">
            <h3 style={{ color: "#668fca" }}>QR Code</h3>
          </div>
          <div className="popUp-close">
            <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} />
          </div>
        </div>
        <div className="popUp-body" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <QRCodeCanvas
            value={qrValue}
            size={180}
            bgColor="#ffffff"
            fgColor="#000000"
            level="H"
          />
        </div>
        <div className="buttons row" style={{ gap: "5px", justifyContent: "center", padding: "40px 40px 20px 40px" }}>
          <button id="add-family" className="save row" onClick={handleDownload}>
            DOWNLOAD
          </button>
          <button id="add-family" className="cancel row" onClick={onClose}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ID container component with fixed size
 */
export function IdContainer({ label, children, width = 375, height = 600 }) {
  return (
    <div 
      className="id-container centeredStyle" 
      style={{ 
        width: `${width}px`, 
        height: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ccc'
      }}
    >
      {children || <span>{label}</span>}
    </div>
  );
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Generates a formatted full name
 */
export function generateFullName(firstName, middleName, lastName) {
  const capitalize = (name) => name?.toUpperCase() || '';
  
  if (middleName && middleName.toLowerCase() !== 'none') {
    const abbreviatedMiddleName = middleName.charAt(0).toUpperCase() + '.';
    return `${capitalize(firstName)} ${abbreviatedMiddleName} ${capitalize(lastName)}`;
  }
  
  return `${capitalize(firstName)} ${capitalize(lastName)}`;
}

/**
 * Capitalizes a name
 */
export function capitalizeName(name) {
  return name?.toUpperCase() || '';
}

// ==================== MAIN COMPONENTS ====================

/**
 * Front ID Generator component with original coordinates
 */
export function FrontIdGenerator({ viewedData, canvasRef }) {
  const textItems = [
    {
      content: `"${viewedData.nickName ? capitalizeName(viewedData.nickName) : capitalizeName(viewedData.firstName)}"`,
      font: 'bold 30px Arial',
      fillStyle: 'white',
      textAlign: 'center',
      x: 187.5, // Canvas width / 2
      y: 460
    },
    {
      content: generateFullName(viewedData.firstName, viewedData.middleName, viewedData.lastName),
      font: 'bold 16px Arial',
      fillStyle: 'white',
      textAlign: 'center',
      x: 187.5,
      y: 490
    },
    {
      content: `EMPLOYEE NO: ${viewedData.employeeId}`,
      font: 'normal 12px Arial',
      fillStyle: 'white',
      textAlign: 'center',
      x: 187.5,
      y: 510
    }
  ];

  const images = [
    {
      src: viewedData.photo ? `data:image/png;base64,${viewedData.photo}` : "images/sample.jpg",
      x: 100,
      y: 135,
      width: 175,
      height: 175,
      border: { color: 'black', width: 1 }
    },
    {
      src: "images/signature.png",
      x: 100,
      y: 530,
      width: 150,
      height: 50
    }
  ];

  return (
    <CanvasImageGenerator
      canvasRef={canvasRef}
      templateSrc="images/id_front.jpg"
      images={images}
      textItems={textItems}
      canvasWidth={375}
      canvasHeight={600}
    />
  );
}

/**
 * Back ID Generator component with original coordinates
 */
export function BackIdGenerator({ viewedData, canvasRef }) {
  const { employeeAccountInformation, employeeEmergencyContacts } = viewedData;
  const emergencyContact = employeeEmergencyContacts?.[0];
  
  const textItems = [];
  
  // Account information
  if (employeeAccountInformation) {
    const accountFields = [
      { key: 'sss', y: 45 },
      { key: 'tin', y: 69 },
      { key: 'philhealth', y: 91 },
      { key: 'pagibig', y: 113 }
    ];
    
    accountFields.forEach(field => {
      if (employeeAccountInformation[field.key]) {
        textItems.push({
          content: employeeAccountInformation[field.key],
          font: 'normal 16px Arial',
          fillStyle: '#f34708',
          textAlign: 'center',
          x: 265,
          y: field.y
        });
      }
    });
  }
  
  // Emergency contact information
  if (emergencyContact) {
    textItems.push(
      {
        content: emergencyContact.name,
        font: 'normal 16px Arial',
        fillStyle: 'white',
        textAlign: 'left',
        x: 175,
        y: 435
      },
      {
        content: emergencyContact.relationship,
        font: 'normal 16px Arial',
        fillStyle: 'white',
        textAlign: 'left',
        x: 175,
        y: 455
      },
      {
        content: emergencyContact.address,
        font: 'normal 16px Arial',
        fillStyle: 'white',
        textAlign: 'left',
        x: 175,
        y: 475,
        maxWidth: 200,
        lineHeight: 20
      },
      {
        content: emergencyContact.contactNumber,
        font: 'normal 16px Arial',
        fillStyle: 'white',
        textAlign: 'left',
        x: 175,
        y: 515
      }
    );
  }

  const images = [
    {
      src: "images/signature.png",
      x: 100,
      y: 250,
      width: 150,
      height: 50
    }
  ];

  return (
    <CanvasImageGenerator
      canvasRef={canvasRef}
      templateSrc="images/id_back.jpg"
      images={images}
      textItems={textItems}
      canvasWidth={375}
      canvasHeight={600}
    />
  );
}

/**
 * Main Generate ID Tab component
 */
export function GenerateIdTab({ viewedData }) {
  const [isGenerate, setIsGenerate] = useState(false);
  const [qrVisible, setQrVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Use the existing GeneralMessage hook
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const canvasRef_front = useRef(null);
  const canvasRef_back = useRef(null);

  const generateQRCode = () => setQrVisible(true);
  const closeQRCodeForm = () => setQrVisible(false);

  const qrValue = JSON.stringify({
    id: viewedData?.id,
    employeeId: viewedData?.employeeId,
  });

  const downloadImage = async () => {
    const canvas_front = canvasRef_front.current;
    const canvas_back = canvasRef_back.current;

    if (!canvas_front || !canvas_back) return;

    const frontBlob = await new Promise((resolve) => canvas_front.toBlob(resolve, 'image/jpeg'));
    const backBlob = await new Promise((resolve) => canvas_back.toBlob(resolve, 'image/jpeg'));

    const formData = new FormData();
    formData.append(
      'frontImage',
      frontBlob,
      `${viewedData.lastName.toLowerCase()}_${viewedData.firstName.toLowerCase()}_id_front.jpg`
    );
    formData.append(
      'backImage',
      backBlob,
      `${viewedData.lastName.toLowerCase()}_${viewedData.firstName.toLowerCase()}_id_back.jpg`
    );

    try {
      setLoading(true);
      
      // change this into their printer....
      const response = await axios.post('ht tp://161.49.93.114:8081/api/PrintId/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.status !== 200) {
        throw new Error('Error uploading images');
      }

      showGeneralMessage("Print starting....", true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Upload failed';
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="family-tab">
        <div className="family-tab-top row">
          <div className='row' style={{ gap: "5px" }}>
            {CreateButton(
              "addEmployee-family-add",
              "generate-submit row",
              generateQRCode,
              "GENERATE QR",
              QrCodeIcon
            )}
            {CreateButton(
              "addEmployee-family-add",
              "generate-submit row",
              () => setIsGenerate(true),
              "GENERATE ID",
              SyncIcon
            )}
          </div>
          <div style={{ marginLeft: "5px" }}>
            {isGenerate && CreateButton(
              "addEmployee-family-add",
              "print-button row",
              downloadImage,
              "PRINT",
              LocalPrintshopIcon
            )}
          </div>
        </div>
        
        <div className="family-tab-body">
          <div className="generate-id-body-container row centeredStyle" style={{ 
            padding: 20, 
            justifyContent: "space-between",
            flexWrap: 'wrap' 
          }}>
            <IdContainer label="Front" width={375} height={600}>
              {isGenerate && (
                <FrontIdGenerator
                  viewedData={viewedData}
                  canvasRef={canvasRef_front}
                />
              )}
            </IdContainer>
            
            <IdContainer label="Back" width={375} height={600}>
              {isGenerate && (
                <BackIdGenerator
                  viewedData={viewedData}
                  canvasRef={canvasRef_back}
                />
              )}
            </IdContainer>
          </div>
        </div>
        
        {/* Use existing GeneralMessage component */}
        <GeneralMessage
          form={showMessage}
          message={message}
          isSuccess={isSuccess}
        />
        
        {/* Use existing LoadingSpinner component */}
        <LoadingSpinner loading={loading} />
      </div>

      <QRCodeModal
        isOpen={qrVisible}
        qrValue={qrValue}
        onClose={closeQRCodeForm}
      />
    </>
  );
}